import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import classNames from '../../../utils/classNames';
import { ReactComponent as RefreshIcon } from '../../../images/refresh-button-icon.svg';
import './index.scss';

/**
 * One full rotation = 900ms (see keyframe `refresh-button-spin`). We guarantee
 * the icon spins for at least this long after each click even when the backing
 * fetch resolves faster, so the animation is always perceivable.
 */
const MIN_SPIN_MS = 900;

export default function RefreshButton({
    onClick = undefined,
    ariaLabel,
    className = '',
    disabled = false,
    spinning = false,
    type = 'button',
    title = undefined,
}) {
    const [clickSpin, setClickSpin] = useState(false);
    const clearTimerRef = useRef(null);

    useEffect(() => () => {
        if (clearTimerRef.current) {
            clearTimeout(clearTimerRef.current);
            clearTimerRef.current = null;
        }
    }, []);

    const handleClick = useCallback((event) => {
        setClickSpin(true);
        if (clearTimerRef.current) {
            clearTimeout(clearTimerRef.current);
        }
        clearTimerRef.current = setTimeout(() => {
            setClickSpin(false);
            clearTimerRef.current = null;
        }, MIN_SPIN_MS);

        if (typeof onClick === 'function') {
            onClick(event);
        }
    }, [onClick]);

    const isSpinning = spinning || clickSpin;

    return (
        <button
            type={type}
            onClick={handleClick}
            aria-label={ariaLabel}
            aria-busy={isSpinning || undefined}
            className={classNames('refresh-button', className)}
            disabled={disabled}
            title={title}
        >
            <span className="refresh-button__content" aria-hidden="true">
                <RefreshIcon
                    className={classNames(
                        'refresh-button__icon',
                        isSpinning && 'refresh-button__icon--spinning',
                    )}
                    focusable="false"
                />
            </span>
        </button>
    );
}

RefreshButton.propTypes = {
    onClick: PropTypes.func,
    ariaLabel: PropTypes.string.isRequired,
    className: PropTypes.string,
    disabled: PropTypes.bool,
    spinning: PropTypes.bool,
    type: PropTypes.oneOf(['button', 'submit', 'reset']),
    title: PropTypes.string,
};
